import {
  getCorePlanAccess,
  getCompanySubscriptions,
} from '@/gql/services/billing.service';
import { ProductKey, SubscriptionStatus } from '@/gql/generated';
import {
  hasLiveStripeCorePlan,
  resolveCorePlan,
  resolveMigratedCorePlan,
} from '@/hooks/useCompanyBillingInfo/planKey';

export interface PlanPackage {
  isBambeePause?: boolean;
  isBambeeStorage?: boolean;
  isBambeeLite?: boolean;
  isBambeeBusiness?: boolean;
  isBambeePlus?: boolean;
}

const isLive = (sub, productKey: ProductKey) =>
  sub.productCode === productKey &&
  (sub.status === SubscriptionStatus.Active ||
    sub.status === SubscriptionStatus.Trialing);

const fromStripeSubscriptions = (info?: any[]): PlanPackage => ({
  isBambeePause: info?.some((sub) => isLive(sub, ProductKey.Pause)),
  isBambeeStorage: info?.some((sub) => isLive(sub, ProductKey.Storage)),
  isBambeeLite: info?.some((sub) => isLive(sub, ProductKey.Lite)),
  isBambeeBusiness: info?.some((sub) => isLive(sub, ProductKey.Core)),
  isBambeePlus: info?.some((sub) => isLive(sub, ProductKey.Plus)),
});

// The answer is cached for the session, keyed by who it was fetched for, so a
// different company never reuses another company's answer.
let cachedKey: string | undefined;
let cachedPackage: PlanPackage | undefined;

// Flags for a plan resolved from getBillingInfo. Lite, Plus and Storage are
// Stripe-only packages.
const fromCorePlan = (
  plan: ReturnType<typeof resolveCorePlan>
): PlanPackage => ({
  isBambeePause: plan === ProductKey.Pause,
  isBambeeStorage: false,
  isBambeeLite: false,
  isBambeeBusiness: plan === ProductKey.Core,
  isBambeePlus: false,
});

const getStripePlanPackage = async (
  stripeCustomerId: string,
  companyId?: string
): Promise<PlanPackage> => {
  const key = `stripe:${stripeCustomerId}`;

  if (cachedKey === key && cachedPackage) {
    return cachedPackage;
  }

  const info = (await getCompanySubscriptions({ stripeCustomerId }))?.data;
  let planPackage = fromStripeSubscriptions(info);
  let cacheable = !!info;

  // A company migrated to Recurly keeps its Stripe customer, but its Stripe
  // core subscription is cancelled (BIO-2085). Only then is getBillingInfo
  // asked, and only a RECURLY answer is used. A STRIPE answer, no answer or
  // an error keeps today's flags.
  if (info && companyId && !hasLiveStripeCorePlan(info)) {
    const billingInfo = await getCorePlanAccess(companyId);
    const plan = resolveMigratedCorePlan(billingInfo?.data?.coreSubscription);

    if (plan) {
      planPackage = fromCorePlan(plan);
    }

    // A failed lookup is retried on the next call.
    cacheable = !!billingInfo?.data;
  }

  // As before BIO-2085: a failed fetch is retried on the next call.
  if (cacheable) {
    cachedKey = key;
    cachedPackage = planPackage;
  }

  return planPackage;
};

// A company with no Stripe customer (Recurly-only, BIO-2085) has no Stripe
// subscriptions to list, so its plan comes from getBillingInfo: Core, Elite,
// or Pause for a paused Recurly subscription.
const getBillingInfoPlanPackage = async (
  companyId: string
): Promise<PlanPackage> => {
  const key = `company:${companyId}`;

  if (cachedKey === key && cachedPackage) {
    return cachedPackage;
  }

  const coreSubscription = (await getCorePlanAccess(companyId))?.data
    ?.coreSubscription;
  const plan = resolveCorePlan(coreSubscription);
  const planPackage = fromCorePlan(plan);

  // Only cache a real plan. A missing or failed answer is fetched again on
  // the next call, so a company that subscribes mid-session is not stuck
  // planless.
  if (plan) {
    cachedKey = key;
    cachedPackage = planPackage;
  }

  return planPackage;
};

export const resetPlanPackageCache = () => {
  cachedKey = undefined;
  cachedPackage = undefined;
};

/**
 * Plan flags for the route guards. Pass the company's Stripe customer id when
 * it has one (unchanged behaviour), and its company id so a company with no
 * Stripe customer resolves from billing-service instead (BIO-2085).
 */
export default async (
  stripeId?: string,
  companyId?: string
): Promise<PlanPackage> => {
  if (stripeId) {
    return getStripePlanPackage(stripeId, companyId);
  }

  if (companyId) {
    return getBillingInfoPlanPackage(companyId);
  }

  return {};
};
